// Non-standard properties exposed by Safari on iOS/iPadOS.
type SafariNavigator = Navigator & {
  standalone?: boolean;
  maxTouchPoints: number;
};

const PWA_INITIALIZED_KEY = 'ios_pwa_initialized';

// Auth-related storage keys wiped on first PWA launch. Kept as an explicit
// allowlist so unrelated state (cookie-consent, newsletter throttling, UI
// preferences) survives the cleanup.
const AUTH_LOCAL_STORAGE_KEYS = ['user_display', 'current_path'];
const AUTH_SESSION_STORAGE_KEYS = ['silent_login', 'pkce_verifier'];

/**
 * On the very first launch of an installed Apple home-screen PWA, wipe any
 * auth state that may have carried over from the Safari session so the user
 * starts logged out and can perform a fresh login.
 *
 * Runs for an installed home-screen PWA on any Apple device:
 * - iPhone / iPad / iPod report a UA containing "iPhone"/"iPad"/"iPod".
 * - Modern iPads report as "Macintosh"; they are treated as iPad-like via
 *   touch support (`maxTouchPoints > 1`).
 * - Desktop macOS is also included so Safari/Chrome PWAs on macOS are covered.
 * - `display-mode: standalone` / `navigator.standalone` confirm the app was
 *   launched from the home screen rather than a normal Safari tab.
 *
 * Runs at most once per PWA install. Clears the auth-related storage keys and
 * service-worker caches, and asks the server to clear the auth cookies
 * (`is_logged_in` plus the httpOnly session tokens) — these are httpOnly, so JS
 * cannot clear them directly. The once-per-install flag is only persisted after
 * the server confirms it cleared the cookies, so a failed/offline first launch
 * retries on the next launch.
 *
 * @returns `true` when the client-side cleanup ran, otherwise `false`.
 */
export const clearIOSPWAFirstRunAuth = async (): Promise<boolean> => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const nav = navigator as SafariNavigator;
  const agent = nav.userAgent || '';

  // Detect Apple devices:
  const isIOS = /iP(hone|ad|od)/.test(agent);
  const isMacOS = /Macintosh/.test(agent);

  const isIPadLikeMac =
    isMacOS && 'maxTouchPoints' in nav && (nav.maxTouchPoints ?? 0) > 1;

  const isAppleMobileLike = isIOS || isIPadLikeMac;
  const isAppleDevice = isAppleMobileLike || isMacOS;

  // Check if running as a standalone/PWA app (Safari, Chrome, etc.)
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    nav.standalone === true;

  // We now run for *any* Apple device in standalone/PWA mode
  const isApplePWA = isStandalone && isAppleDevice;
  if (!isApplePWA) return false;

  try {
    if (localStorage.getItem(PWA_INITIALIZED_KEY)) return false;

    // Clear only auth-related web storage — leave unrelated state intact.
    AUTH_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    AUTH_SESSION_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));

    // Clear service-worker caches.
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }

    // The auth cookies (`is_logged_in` + session tokens) are httpOnly, so JS
    // cannot clear them. Ask the server to expire them via Set-Cookie. Only the
    // cookie-clearing side effect matters — the returned B2C logout URL is
    // ignored and no redirect is performed.
    let serverCleared = false;
    try {
      const res = await fetch('/api/auth/logout');
      serverCleared = res.ok;
    } catch {
      serverCleared = false;
    }

    // Only mark as initialized once the server actually cleared its cookies, so
    // a failed/offline first launch retries on the next launch.
    if (serverCleared) {
      localStorage.setItem(PWA_INITIALIZED_KEY, 'true');
    }

    return true;
  } catch (error) {
    console.error('[PWA Init] iOS first-run auth cleanup failed', error);
    return false;
  }
};

